import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { SUPABASE_ANON_KEY, FUNCTIONS_URL } from '../services/supabaseConfig';
import './DevelopmentReportPage.css';

interface TrainingEntry {
  id: string;
  player_id: string;
  player_name: string;
  attribute: string;
  old_value: number;
  new_value: number;
  trained_at: string;
}

const ATTR_LABELS: Record<string, string> = {
  power: 'Power',
  contact: 'Contact',
  discipline: 'Discipline',
  speed: 'Speed',
  batting_attr: 'Batting',
  glove_attr: 'Glove',
  range_attr: 'Range',
  velocity: 'Velocity',
  control: 'Control',
  movement: 'Movement',
  deception: 'Deception',
  stamina: 'Stamina',
};

const DevelopmentReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<TrainingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [attrFilter, setAttrFilter] = useState<string>('all');
  const [trainingStatus, setTrainingStatus] = useState<any>(null);

  useEffect(() => {
    loadTrainingHistory();
  }, []);

  const loadTrainingHistory = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId || !isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }

    // Load training log for this user
    const { data } = await supabase
      .from('training_log')
      .select('*')
      .eq('user_id', userId)
      .order('trained_at', { ascending: false })
      .limit(100);

    if (data) {
      // Fetch player names for the logs
      const playerIds = Array.from(new Set(data.map((d: any) => d.player_id)));
      const { data: players } = await supabase
        .from('players')
        .select('id, first_name, last_name')
        .in('id', playerIds);

      const nameMap = new Map((players || []).map((p: any) => [p.id, `${p.first_name} ${p.last_name}`]));

      const formatted: TrainingEntry[] = data.map((d: any) => ({
        id: d.id,
        player_id: d.player_id,
        player_name: nameMap.get(d.player_id) || 'Unknown',
        attribute: d.attribute,
        old_value: d.old_value,
        new_value: d.new_value,
        trained_at: d.trained_at,
      }));

      setEntries(formatted);
    }

    // Also load training status
    try {
      const res = await fetch(`${FUNCTIONS_URL}/training-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ userId }),
      });
      const status = await res.json();
      setTrainingStatus(status);
    } catch (e) {
      console.error('Failed to load training status:', e);
    }

    setLoading(false);
  };

  const filtered = entries.filter(e => {
    if (attrFilter !== 'all' && e.attribute !== attrFilter) return false;
    return true;
  });

  // Group entries by date
  const grouped = filtered.reduce((acc: Record<string, TrainingEntry[]>, entry) => {
    const date = new Date(entry.trained_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  // Unique attributes for filter
  const attributes = Array.from(new Set(entries.map(e => e.attribute)));

  return (
    <div className="dev-report-page">
      <div className="dev-report-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1>🏋️ Training History</h1>
      </div>

      {trainingStatus && (
        <div className="training-summary">
          <div className="summary-stat">
            <span className="summary-label">Players</span>
            <span className="summary-value">{trainingStatus.players?.length || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="summary-label">Can Train Today</span>
            <span className="summary-value">{trainingStatus.players?.filter((p: any) => p.canTrainToday).length || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="summary-label">Train Amount</span>
            <span className="summary-value">+{trainingStatus.trainAmount || 0.5}</span>
          </div>
          <div className="summary-stat">
            <span className="summary-label">Streak Limit</span>
            <span className="summary-value">{trainingStatus.maxStreak || 3} days</span>
          </div>
        </div>
      )}

      <div className="dev-filters">
        <select value={attrFilter} onChange={e => setAttrFilter(e.target.value)}>
          <option value="all">All Attributes</option>
          {attributes.map(attr => (
            <option key={attr} value={attr}>{ATTR_LABELS[attr] || attr}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="dev-loading">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="dev-empty">
          <p>No training history yet.</p>
          <p>Visit your Agent's Office to start training your players!</p>
        </div>
      ) : (
        <div className="dev-entries">
          {Object.entries(grouped).map(([date, dateEntries]) => (
            <div key={date} className="dev-date-group">
              <h3 className="dev-date-header">{date}</h3>
              <div className="dev-cards">
                {dateEntries.map(entry => (
                  <div key={entry.id} className="dev-card">
                    <div className="dev-card-header">
                      <span className="dev-player-name">{entry.player_name}</span>
                      <span className="dev-attr-name">{ATTR_LABELS[entry.attribute] || entry.attribute}</span>
                    </div>
                    <div className="dev-card-change">
                      <span className="dev-old">{entry.old_value}</span>
                      <span className="dev-arrow">→</span>
                      <span className="dev-new">+{entry.new_value}</span>
                      <span className="dev-delta">(+0.5)</span>
                    </div>
                    <div className="dev-card-time">
                      {new Date(entry.trained_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DevelopmentReportPage;
